import React from 'react';
import { Info, AlertTriangle, AlertCircle, CheckCircle2, X } from 'lucide-react';
import { cn } from '../../utils/cn';
import { IconButton } from './IconButton';

export type BannerTone = 'info' | 'warning' | 'danger' | 'success';

export interface BannerProps {
  tone: BannerTone;
  title?: string;
  children?: React.ReactNode;
  action?: React.ReactNode;
  onDismiss?: () => void;
  icon?: React.ReactNode;
  className?: string;
}

const toneClasses: Record<BannerTone, string> = {
  info: 'bg-brand-soft text-on-brand-soft',
  warning: 'bg-warning-surface text-warning-text',
  danger: 'bg-danger-surface text-danger-text',
  success: 'bg-success-surface text-success-text',
};

const defaultIcons: Record<BannerTone, React.ReactNode> = {
  info: <Info className="h-5 w-5" aria-hidden="true" />,
  warning: <AlertTriangle className="h-5 w-5" aria-hidden="true" />,
  danger: <AlertCircle className="h-5 w-5" aria-hidden="true" />,
  success: <CheckCircle2 className="h-5 w-5" aria-hidden="true" />,
};

export const Banner: React.FC<BannerProps> = ({ tone, title, children, action, onDismiss, icon, className }) => {
  const role = tone === 'warning' || tone === 'danger' ? 'alert' : 'status';
  return (
    <div role={role} className={cn('flex gap-3 rounded-card p-4', toneClasses[tone], className)}>
      <span className="shrink-0">{icon ?? defaultIcons[tone]}</span>
      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className="mt-0.5 text-sm">{children}</div>}
        {action && <div className="mt-2">{action}</div>}
      </div>
      {onDismiss && (
        <IconButton
          label="Hinweis schließen"
          icon={<X className="h-4 w-4" />}
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          className="-m-1 shrink-0"
        />
      )}
    </div>
  );
};
